/**
 * MoonClerk Capital landing page — /capital/ (handoff from Amitabh, 25 Oct 2026).
 *
 * Rendered as the whole body of src/pages/capital.js, inside the site's V2ChromeLayout
 * (nav, footer, SEO). Styles live in ./capital.css (scoped under .mcc-) and are imported here.
 *
 * COPY IS COMPLIANCE-LOCKED: every string below is the text Stripe reviewed in compliance
 * submission v3 (16 Oct 2026, Asset 1), including the en dashes, the asterisks and the curly
 * apostrophes. The two disclosures are v4's (Stripe's review, 27 Oct 2026); the three "pre-qualified offer" lines are v5's (banking partner, 30 Oct 2026). Do not reword, shorten or add claims without a new Stripe review. That is also
 * why the copy lives here rather than in Dato: an editor must not be able to drift it.
 */
import React from "react"

import "./capital.css"

export const ELIGIBILITY_URL = "https://app.moonclerk.com/financing"
export const FAQ_URL =
  "https://help.moonclerk.com/en/articles/17571563-moonclerk-capital-faqs"

const STEPS = [
  {
    title: "Simplified application process",
    body: "Apply in a few clicks! Eligibility is based on factors like your payment volume and history on MoonClerk.",
  },
  {
    title: "Get funds quickly",
    body: "If you’re approved, receive your funds in as little as one to two business days directly into your linked account.",
  },
  {
    title: "Clear terms–no surprises",
    body: "You’ll view terms up front when you select the pre-qualified offer amount that’s right for your business, so you’ll know exactly what to expect from MoonClerk Capital.",
  },
  {
    title: "Pay as you earn for stress-free financing*",
    body: "With MoonClerk Capital, you automatically pay the loan or merchant cash advance with a fixed percentage of your daily sales until the total amount is paid.",
  },
]

const USES = [
  {
    title: "Invest where it matters most",
    body: "Whether that’s purchasing equipment or new inventory, investing in marketing, or managing cash flow, MoonClerk Capital gives you the financial support to grow.",
  },
  {
    title: "Tailored solutions for your business",
    body: "Pre-qualified offers are based on your business’s performance data, providing personalized pre-qualified offers to meet your specific business needs.",
  },
  {
    title: "Customize the size of your pre-qualified offer",
    body: "Based on your business needs. You’ll only pay the cost of your financing plus one flat fee that never changes, there are no compounding interest charges or late fees.",
  },
]

const FINANCING_TYPES =
  "Stripe Capital offers financing types that include loans and merchant cash advances. All financing applications are subject to review prior to approval. In the US, Stripe Capital loans are issued by Celtic Bank and YouLend provides Stripe Capital merchant cash advances."
// The asterisk is part of the text: it is what the page's "*" claims point to.
const LOANS_DISCLOSURE =
  "*For MoonClerk Capital Loans, you’ll have a minimum amount due each repayment period, and if the minimum amount that you pay through sales doesn’t meet the minimum, your account will be automatically debited the remaining amount at the end of the period."

// Since v4 the on-page disclaimer and the footer carry the same two paragraphs in the same order.
const FOOTER_LEGAL_HTML = `<div class="mcc-footer-legal"><p>${FINANCING_TYPES}</p><p>${LOANS_DISCLOSURE}</p></div>`

const Capital = () => (
  // data-mc-how-it-works-href: mc-chrome.js points the footer's "How it works" link at the
  // steps section below instead of /how-it-works/ (Amitabh, Basecamp, 27 Oct 2026).
  <div className="mcc-page" data-mc-how-it-works-href="#how-it-works">
    {/* Section 1 — Hero */}
    <section className="mcc-hero">
      <div className="mcc-wrap mcc-hero__grid">
        <div>
          {/* Co-brand mark: MoonClerk logo | Stripe wordmark (Stripe's logo kit), equal
              height, and no "Powered by" wording. Each logo carries its own alt text. */}
          <div className="mcc-cobrand">
            <img
              src="/assets/moonclerk-logo-white.png"
              alt="MoonClerk"
              width="320"
              height="46"
            />
            <span className="mcc-cobrand__divider" aria-hidden="true" />
            <img
              src="/assets/capital/stripe-wordmark-white.svg"
              alt="Stripe"
              width="360"
              height="151"
            />
          </div>
          <h1>
            Get access to fast funding for your business – with MoonClerk
            Capital
          </h1>
          <p className="mcc-hero__sub">
            Quick access to funds to help grow your business–without the hassle
            of traditional loans.
          </p>
          <div className="mcc-hero__ctas">
            <a className="mcc-btn mcc-btn--primary" href={ELIGIBILITY_URL}>
              Check eligibility
            </a>
            <a className="mcc-btn mcc-btn--outline" href={FAQ_URL}>
              FAQs
            </a>
          </div>
        </div>
        <div className="mcc-hero__shot">
          <img
            src="/assets/capital/capital-financing-page.jpg"
            width="1440"
            height="812"
            alt="The MoonClerk Capital Financing page inside the MoonClerk dashboard, showing a prequalified financing offer and how it works"
            loading="eager"
          />
        </div>
      </div>
    </section>

    {/* Section 2 — How it works */}
    <section
      className="mcc-how"
      id="how-it-works"
      aria-labelledby="mcc-how-title"
    >
      <div className="mcc-wrap">
        <h2 className="mcc-h2" id="mcc-how-title">
          How it works
        </h2>
        <p className="mcc-lead">Fast funding and flexible payments*</p>
        <ol className="mcc-steps">
          {STEPS.map((step, index) => (
            <li className="mcc-step" key={step.title}>
              <span className="mcc-step__num" aria-hidden="true">
                {index + 1}
              </span>
              <h3>{step.title}</h3>
              <p>{step.body}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>

    {/* Section 3 — Use of funds */}
    <section className="mcc-uses" aria-labelledby="mcc-uses-title">
      <div className="mcc-wrap">
        <h2 className="mcc-h2" id="mcc-uses-title">
          Use funds to support your business’s growth
        </h2>
        <p className="mcc-lead">
          If approved, access flexible financing for anything your business
          needs.
        </p>
        <ul className="mcc-cards">
          {USES.map((card) => (
            <li className="mcc-card" key={card.title}>
              <h3>{card.title}</h3>
              <p>{card.body}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>

    {/* Financial disclaimer — required directly below Section 3, before the FAQ and footer */}
    <section className="mcc-disclaimer" aria-label="Financing disclosures">
      <div className="mcc-wrap">
        <p>{FINANCING_TYPES}</p>
        <p>{LOANS_DISCLOSURE}</p>
      </div>
    </section>

    {/* Section 4 — FAQ pointer */}
    <section className="mcc-faq" aria-labelledby="mcc-faq-title">
      <div className="mcc-wrap">
        <h2 className="mcc-h2" id="mcc-faq-title">
          Frequently asked questions
        </h2>
        <p>
          Have questions about eligibility, fees, or payments? Read the
          MoonClerk Capital FAQs.
        </p>
        <a className="mcc-btn mcc-btn--outline" href={FAQ_URL}>
          Read the FAQs
        </a>
      </div>
    </section>

    {/* Required footer text. static/assets/mc-chrome.js injects the site footer client-side
        and copies this template's content in above the copyright row. A <template> is inert,
        so React keeps owning this node and the script only ever touches its copy. It is set
        as HTML because React cannot hydrate JSX children of a <template>: the parser moves them
        into template.content, so React would find no children and re-render them. */}
    <template
      id="mc-footer-legal"
      dangerouslySetInnerHTML={{ __html: FOOTER_LEGAL_HTML }}
    />
  </div>
)

export default Capital
